import { useAuth0, User } from "@auth0/auth0-react";
import { FC, ReactNode, useMemo, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { useAppContext } from "../../../contexts/AppContext";
import { useMenuExpanded } from "../../../hooks/useMenuExpanded";
import { appRoutes, externalRoutes } from "../../../utils/routes";
import { Button } from "../ButtonV2/Button";
import { UserAvatar } from "../UserAvatar";
import { useUserPreferredPage } from "../../../hooks/useUserPreferredPage";
import { useContactContext } from "../../../contexts/ContactContext";
import { useAbility } from "../../casl/hooks/useAbility";
import { UndermapsApplicationSubject } from "../../casl/types";
import { CaslVisible } from "../../casl/CaslVisible";
import { JobTypeIcon } from "../../undermaps/common/JobTypeIcon/JobTypeIcon";
import FeatureFlagsList from "../FeatureFlag/FeatureFlagsList";
import {
  Popover,
  PopoverContent,
  PopoverTrigger
} from "../../undermaps/common/Popover/Popover";
import { HomeIcon, UserIcon } from "@heroicons/react/20/solid";
import { ThemeMode } from "../../types";
import {
  UNDERMAPS_MARKETING_WEBSITE_URL,
  UNDERMAPS_TOOLTIP_ID
} from "../../undermaps/constants";
import MenuButton, { UserMenuItem } from "./UserMenuButton";
import { BYDA_SUBDOMAIN } from "../../../hooks/useSubDomain";
import { useAppNavigate } from "../../../applications/hooks/useAppNavigate";
import { BydaIcon } from "../SvgLibrary/BydaLogo";

export type UserMenuProps = {
  children?: ReactNode;
  showIcon: boolean;
  size?: "small" | "regular";
  mode?: ThemeMode;
};

type ApplicationLink = {
  label: UndermapsApplicationSubject | "Undermaps" | "Collect 3D";
  route: string;
  application: UndermapsApplicationSubject | "Undermaps";
  disabled?: boolean;
  disabledTooltipMessage?: string;
};

const APPLICATION_LINKS: ApplicationLink[] = [
  {
    label: "Collect",
    route: appRoutes.COLLECT,
    application: "Collect"
  },
  {
    label: "Transformer",
    route: appRoutes.TRANSFORMER,
    application: "Transformer"
  },
  {
    label: "Edit",
    route: appRoutes.EDIT,
    application: "Edit"
  }
];

const GRAY_COLOUR = "#9CA3AF"; // gray-400

const UserMenu: FC<UserMenuProps> = ({ showIcon, size = "regular", mode }) => {
  const { ref, isMenuExpanded, setMenuExpanded } = useMenuExpanded(false);
  const { openContactModal } = useContactContext();

  const {
    profileAsync: { data: profile, loading: profileLoading }
  } = useAppContext();

  const { applicationAbility } = useAbility();
  const { logout, user, loginWithRedirect } = useAuth0();

  const userMenuLinks = useMemo(() => {
    if (!profile) return [];

    const applicationLinks = APPLICATION_LINKS.filter((app) =>
      applicationAbility.can(
        "use",
        app.application as UndermapsApplicationSubject
      )
    );

    return [
      {
        label: "Undermaps",
        route: appRoutes.UNDERMAPS,
        application: "Undermaps",
        disabled: !applicationAbility.can("use", "Undermaps"),
        disabledTooltipMessage:
          "Your user has not been configured to use this application. Please contact support@undermaps.com to get access."
      } as ApplicationLink,
      ...applicationLinks
    ];
  }, [applicationAbility, profile]);

  const USER_PREFERRED_PAGE_WHITELIST = [
    appRoutes.UNDERMAPS,
    appRoutes.COLLECT
  ];
  return (
    <div className="ml-3 relative z-20 " ref={ref}>
      {!profile && !profileLoading && (
        <div className="flex gap-2">
          <Button
            onClick={() => {
              loginWithRedirect({
                appState: {
                  returnTo: window.location.pathname
                }
              });
            }}
            color="primary"
            size="regular"
            mode="light"
          >
            Login
          </Button>
          <Button
            id="learn-more-button-navbar"
            variant="outlined"
            onClick={() => {
              window.open(
                UNDERMAPS_MARKETING_WEBSITE_URL,
                "_blank",
                "noopener,noreferrer"
              );
            }}
          >
            Learn more
          </Button>
        </div>
      )}
      {profile && (
        <>
          <Popover>
            <PopoverTrigger>
              <button
                type="button"
                id="user-menu-button"
                onClick={() => setMenuExpanded(!isMenuExpanded)}
              >
                <span className="sr-only">Open user menu</span>
                <UserAvatar
                  size={size}
                  user={user}
                  showIcon={showIcon}
                  mode={mode}
                />
              </button>
            </PopoverTrigger>
            <PopoverContent>
              <UserMenuContent>
                <CaslVisible type="Undermaps" action="admin">
                  <div className="bg-gray-100 px-4 pt-2">
                    <p className="text-xs text-gray-700">Admin links:</p>
                    <Link
                      className="block px-4 py-2 text-sm text-gray-700"
                      to={{
                        pathname: appRoutes.ADMIN
                      }}
                      onClick={() => setMenuExpanded(!isMenuExpanded)}
                    >
                      Admin
                    </Link>
                  </div>
                </CaslVisible>

                <UserMenuBlock>
                  {user && <UserEmail user={user} />}
                  <HomeLink />
                </UserMenuBlock>
                <UserMenuBlock>
                  {userMenuLinks.map((app) => {
                    return (
                      <ApplicationMenuLink
                        application={app}
                        key={app.label}
                        setPreferredPage={USER_PREFERRED_PAGE_WHITELIST.includes(
                          app.route
                        )}
                      />
                    );
                  })}
                  <BydaLink />
                </UserMenuBlock>
                <CaslVisible type="Undermaps" action="internal">
                  <div className="bg-cyan-100 px-4 pt-2 drop-shadow">
                    <p className="text-xs text-gray-700 font-semibold">
                      Experimental Features
                    </p>
                    <FeatureFlagsList />
                  </div>
                </CaslVisible>
                <UserMenuBlock>
                  <MenuButton
                    as="a"
                    href={externalRoutes.UNDERMAPS_USER_DOCS}
                    target="_blank"
                    rel="noopener noreferrer"
                    label="User documentation"
                  />

                  <MenuButton
                    onClick={() => openContactModal()}
                    label="Contact us"
                  />
                </UserMenuBlock>
                <UserMenuBlock>
                  <MenuButton
                    label="Sign out"
                    id="sign-out"
                    onClick={() => logout({ returnTo: window.location.origin })}
                  >
                    Sign out
                  </MenuButton>
                </UserMenuBlock>
              </UserMenuContent>
            </PopoverContent>
          </Popover>
        </>
      )}
    </div>
  );
};

const ApplicationMenuLink: FC<{
  application: ApplicationLink;
  setPreferredPage: boolean;
}> = ({ application, setPreferredPage }) => {
  const {
    route,
    application: appName,
    label,
    disabled,
    disabledTooltipMessage
  } = application;
  const [isHighlighted, setIsHighlighted] = useState<boolean>(false);

  const location = useLocation();
  const { setUserPreferredPage } = useUserPreferredPage("", []);

  return (
    <>
      <MenuButton
        as={Link}
        disabled={disabled}
        onMouseEnter={() => setIsHighlighted(true)}
        onMouseLeave={() => setIsHighlighted(false)}
        onClick={() => {
          if (setPreferredPage) {
            setUserPreferredPage(route);
          }
        }}
        to={{
          pathname: route,
          search: location.search
        }}
        label={label}
        customIcon={
          <JobTypeIcon
            projectType={appName}
            size={6}
            bgColour="none"
            logoColour={isHighlighted ? undefined : GRAY_COLOUR}
          />
        }
        {...(disabled &&
          disabledTooltipMessage && {
            "data-tooltip-id": UNDERMAPS_TOOLTIP_ID,
            "data-tooltip-content": disabledTooltipMessage
          })}
      />
    </>
  );
};

export const UserMenuContent: FC<{ children: ReactNode }> = ({ children }) => {
  return (
    <div className="w-80 rounded-md shadow-lg ring-1 ring-black ring-opacity-5 focus:outline-none z-10 divide-y py-1">
      {children}
    </div>
  );
};

export const UserEmail: FC<{ user: User }> = ({ user }) => {
  return <UserMenuItem HeroIcon={UserIcon} label={user.email} />;
};

const HomeLink = () => {
  return (
    <MenuButton
      as={Link}
      to={appRoutes.HOME}
      HeroIcon={HomeIcon}
      label="Home"
    />
  );
};

const BydaLink: FC = () => {
  const { toLandingPage } = useAppNavigate(BYDA_SUBDOMAIN);

  return (
    <CaslVisible
      type="Organisation"
      action="view-public"
      organisation={{ slug: BYDA_SUBDOMAIN }}
    >
      <MenuButton
        as="a"
        href={toLandingPage().path}
        label="BYDA Digital Utility Portal"
        customIcon={<BydaIcon className="h-5 w-5" />}
      />
    </CaslVisible>
  );
};

export const UserMenuBlock: FC<{ children: ReactNode }> = ({ children }) => {
  return (
    <>
      <div className="flex flex-col py-1 ">{children}</div>
    </>
  );
};

export default UserMenu;
